/**
 * StagingTable — slim activities table for the in-memory entry step.
 *
 * Mirrors `ActivityTable` row styling (icon + scope badge + activity name +
 * type subtitle, region under asset, etc.) but drops the columns that
 * only make sense post-persistence (CO₂e, Status, Added). Adds a Factor
 * column with an inline picker so the user can swap the auto-resolved
 * factor before submit.
 *
 * Kept separate from `ActivityTable` so that component stays uncluttered
 * for its many other server-backed consumers.
 */

import { useState } from 'react';
import { Table } from 'reactstrap';
import { Search, FileSpreadsheet, PencilLine } from 'lucide-react';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@g17eco/ui-modern/components/ui/dropdown-menu';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { EmissionScope, type AlternativeFactor } from '../../types/emissions';
import { SCOPE_TAILWIND } from '../../config/scopeBranding';
import { ACTIVITY_TYPE_COLORS, ACTIVITY_TYPE_FA_ICONS, ACTIVITY_TYPE_LABELS } from '../../config/activityTypeBranding';
import { getActivitySubtype, getActivityUnitLabel, getClimatiqUnitType } from '../../utils/activityUtils';
import type { BulkGroup, StagedRow } from './types';
import { REGION_OPTIONS } from './types';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { FactorSearchModal } from '../factor-search/FactorSearchModal';
import {
  FUEL_TYPE_LABELS,
  GAS_TYPE_LABELS,
  PROCESS_TYPE_LABELS,
  VEHICLE_TYPE_LABELS,
} from '@g17eco/emissions-config';

const SCOPE_LABELS: Record<EmissionScope, string> = {
  [EmissionScope.Scope1]: 'Scope 1',
  [EmissionScope.Scope2]: 'Scope 2',
  [EmissionScope.Scope3]: 'Scope 3',
};

const FACTOR_SOURCE_LABELS: Record<string, string> = {
  defra: 'DEFRA',
  iea: 'IEA',
  epa: 'EPA',
  climatiq: 'Climatiq',
};

const formatFactorSource = (source: string): string =>
  FACTOR_SOURCE_LABELS[source.toLowerCase()] ?? source.toUpperCase();

/**
 * Build the initial Climatiq search query string for the FactorSearchModal
 * from a row's classification. Mirrors the prefill logic on the activity
 * detail page so the staging table opens the modal already pointed at
 * the most-likely factor name.
 */
const buildInitialFactorQuery = (row: StagedRow): string => {
  const r = row.request;
  if (row.overrideFactorName) return row.overrideFactorName;
  if (row.resolvedFactorName) return row.resolvedFactorName;
  if (r.fuelType) return FUEL_TYPE_LABELS[r.fuelType] ?? r.fuelType;
  if (r.refrigerantType) return GAS_TYPE_LABELS[r.refrigerantType] ?? r.refrigerantType;
  if (r.processType) return PROCESS_TYPE_LABELS[r.processType] ?? r.processType;
  if (r.vehicleType) return VEHICLE_TYPE_LABELS[r.vehicleType] ?? r.vehicleType;
  return r.activityType ? r.activityType.replace(/_/g, ' ') : '';
};

/**
 * Render the factor cell as a Button that opens the shared
 * `FactorSearchModal`. Replaces the previous inline DropdownMenu picker:
 * the alternatives the auto-resolve cached on the row aren't always
 * what the user wants, and the dropdown couldn't expose Climatiq's full
 * catalogue. The modal also lets us keep a single "search factors" UX
 * across the staging table and the activity detail page.
 *
 * Empty / not-yet-classified states still render their own copy ("—",
 * "Resolving…", "No factor found") with a Choose button so the user
 * can manually pick a factor when auto-resolve returns nothing.
 */
const FactorPickerCell = ({
  row,
  initiativeId,
  onPickAlternative,
  onRevertOverride,
}: {
  row: StagedRow;
  initiativeId: string;
  onPickAlternative: (rowId: string, factor: AlternativeFactor) => void;
  onRevertOverride: (rowId: string) => void;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const activeId = row.overrideFactorId ?? row.resolvedFactorId;
  const activeName = row.overrideFactorName ?? row.resolvedFactorName;
  const isOverride = Boolean(row.overrideFactorId);
  const alternatives = row.factorAlternatives ?? [];
  const activeAlt = activeId ? alternatives.find((alt) => alt.id === activeId) : undefined;
  const hasMinimumFields = Boolean(
    row.request.scope && row.request.activityType && row.request.region && row.request.unit,
  );

  // The modal needs a scope + activityType to query. While the row is
  // still being filled in (rare here — the table only renders saved
  // rows) we fall back to the dash placeholder.
  const canOpenModal = hasMinimumFields;

  // Build the metadata line shown on row 2 of the trigger.
  const metadataLine = activeAlt
    ? [
        formatFactorSource(activeAlt.source),
        activeAlt.region,
        String(activeAlt.year),
        ...(activeAlt.factor !== null ? [`${activeAlt.factor} ${getActivityUnitLabel(activeAlt.unit)}`] : []),
      ].join(' · ')
    : null;

  const initialQuery = buildInitialFactorQuery(row);
  const initialYear = row.request.periodStart
    ? new Date(row.request.periodStart).getFullYear() || undefined
    : undefined;
  // Pin the Climatiq unit_type to the row's unit family so the modal
  // doesn't surface factors that can't be applied (e.g. a Volume factor
  // when the activity is logged in kWh). Empty array => no filter,
  // which is also what FactorSearchModal expects when omitted.
  const unitTypeFilter = getClimatiqUnitType(row.request.unit);

  const modal = canOpenModal ? (
    <FactorSearchModal
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      initiativeId={initiativeId}
      scope={row.request.scope as EmissionScope}
      activityType={row.request.activityType}
      unitType={unitTypeFilter.length > 0 ? unitTypeFilter : undefined}
      initialQuery={initialQuery}
      initialRegion={row.request.region || undefined}
      initialYear={initialYear}
      currentFactorId={activeId}
      regions={REGION_OPTIONS}
      onSelect={async (factor) => {
        onPickAlternative(row.id, factor);
      }}
    />
  ) : null;

  // Empty / classification-incomplete state.
  if (!activeId && !hasMinimumFields) {
    return <span className='tw:text-xs tw:text-muted-foreground'>—</span>;
  }

  // Search ran, found nothing — surface a "Choose factor" button so
  // the user can manually pick one rather than being stuck.
  if (!activeId && row.resolveAttempted) {
    return (
      <>
        <div className='tw:flex tw:flex-col tw:items-start tw:gap-1'>
          <span className='tw:text-xs tw:text-amber-700'>No factor found</span>
          <Button
            variant='link'
            size='xs'
            className='tw:h-auto tw:p-0 tw:text-xs'
            onClick={() => setIsModalOpen(true)}
          >
            <Search className='tw:size-3 tw:mr-1' />
            Choose factor
          </Button>
        </div>
        {modal}
      </>
    );
  }

  if (!activeId) {
    return <span className='tw:text-xs tw:text-muted-foreground'>Resolving…</span>;
  }

  return (
    <>
      <div className='tw:flex tw:items-center tw:gap-1'>
        <Button
          variant='amber-outline'
          size='xs'
          className='tw:h-auto tw:w-100 tw:min-w-0 tw:py-1.5 tw:justify-between tw:gap-2 tw:items-center'
          style={{ maxWidth: 280 }}
          onClick={() => setIsModalOpen(true)}
        >
          <div className='tw:flex tw:flex-col tw:items-start tw:min-w-0 tw:pl-1'>
            <div className='tw:flex tw:items-center tw:gap-1 tw:font-medium tw:truncate tw:max-w-full'>
              <span className='tw:truncate'>{activeName}</span>
              {isOverride && (
                <Badge variant='secondary' className='tw:text-[9px] tw:font-normal tw:ml-1 tw:shrink-0'>
                  override
                </Badge>
              )}
            </div>
            {metadataLine && (
              <div className='tw:text-[10px] tw:text-muted-foreground tw:font-normal tw:truncate tw:max-w-full'>
                {metadataLine}
              </div>
            )}
          </div>
          <Search className='tw:size-4 tw:opacity-60 tw:shrink-0' />
        </Button>
        {isOverride && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                size='icon-sm'
                aria-label='Factor options'
                className='tw:shrink-0'
              >
                <i className='fa fa-ellipsis-v' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem onSelect={() => onRevertOverride(row.id)}>
                <span className='tw:text-xs'>Revert to auto-resolved factor</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setIsModalOpen(true)}>
                <span className='tw:text-xs'>Search factors…</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {modal}
    </>
  );
};

export interface StagingTableProps {
  rows: StagedRow[];
  groupById: Map<string, BulkGroup>;
  /**
   * Initiative id for the per-row factor picker — the picker opens
   * `FactorSearchModal`, which needs an initiative to scope the
   * Climatiq search to.
   */
  initiativeId: string;
  onEdit: (rowId: string) => void;
  onDelete: (rowId: string) => void;
  onPickAlternative: (rowId: string, factor: AlternativeFactor) => void;
  onRevertOverride: (rowId: string) => void;
  /**
   * Re-run factor search for a single row. Clears the row's resolved
   * factor + alternatives + override and asks the factor-preview hook to
   * re-fire its lookup.
   */
  onRetriggerFactorSearch: (rowId: string) => void;
  /**
   * Show the Source column (Manual / bulk-import group). Useful for the
   * Usage Entry step where rows can come from XLSX uploads. The Asset
   * wizard hides it — every row is manual entry. Defaults to `true`.
   */
  showSourceColumn?: boolean;
}

export const StagingTable = ({
  rows,
  groupById,
  initiativeId,
  onEdit,
  onDelete,
  onPickAlternative,
  onRevertOverride,
  onRetriggerFactorSearch,
  showSourceColumn = true,
}: StagingTableProps) => {
  // jsdom guard for tooltips not needed — no UncontrolledTooltip here.
  return (
    <div className='tw:overflow-x-auto'>
      <Table className='tw:mb-0' hover>
        <thead>
          <tr>
            <th>Activity</th>
            {showSourceColumn && <th>Source</th>}
            <th>Period</th>
            <th className='tw:text-right'>Quantity</th>
            <th>Factor</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const r = row.request;
            const activityColor = ACTIVITY_TYPE_COLORS[r.activityType] ?? '#6c757d';
            const activityIcon = ACTIVITY_TYPE_FA_ICONS[r.activityType] ?? 'fa-leaf';
            const activityTypeLabel = ACTIVITY_TYPE_LABELS[r.activityType] ?? r.activityType;
            const scopeTw = SCOPE_TAILWIND[r.scope as EmissionScope];
            const subtype = getActivitySubtype({
              fuelType: r.fuelType || undefined,
              vehicleType: r.vehicleType || undefined,
              refrigerantType: r.refrigerantType || undefined,
            });
            const titleText = r.name || subtype || activityTypeLabel;
            const subtitleText = r.name ? activityTypeLabel : (subtype ? activityTypeLabel : undefined);
            const sourceGroup = row.bulkGroupId ? groupById.get(row.bulkGroupId) : undefined;

            return (
              <tr key={row.id}>
                <td>
                  <div className='tw:flex tw:items-center tw:gap-2'>
                    <div className='tw:flex tw:flex-col tw:items-center tw:gap-1 tw:shrink-0'>
                      <div
                        className='tw:rounded tw:flex tw:items-center tw:justify-center'
                        style={{ width: 32, height: 32, backgroundColor: `${activityColor}15` }}
                      >
                        <i
                          className={`fa ${activityIcon}`}
                          style={{ fontSize: '0.85rem', color: activityColor }}
                        />
                      </div>
                      {scopeTw && (
                        <Badge className={cn('tw:text-[10px] tw:px-1 tw:py-0', scopeTw.iconBg, scopeTw.icon)}>
                          {SCOPE_LABELS[r.scope as EmissionScope]}
                        </Badge>
                      )}
                    </div>
                    <div>
                      <div className='fw-medium'>{titleText}</div>
                      {subtitleText && <small className='text-ThemeTextLight text-capitalize'>{subtitleText}</small>}
                    </div>
                  </div>
                </td>
                {showSourceColumn && (
                  <td>
                    {sourceGroup ? (
                      <span className='tw:inline-flex tw:items-center tw:gap-1 tw:text-xs tw:text-muted-foreground' title={sourceGroup.filename}>
                        <FileSpreadsheet className='tw:size-3' />
                        <span className='tw:truncate' style={{ maxWidth: 140 }}>{sourceGroup.filename}</span>
                      </span>
                    ) : (
                      <span className='tw:inline-flex tw:items-center tw:gap-1 tw:text-xs tw:text-muted-foreground'>
                        <PencilLine className='tw:size-3' />
                        Manual
                      </span>
                    )}
                    <div className='tw:text-xs tw:text-muted-foreground tw:opacity-60'>{r.region}</div>
                  </td>
                )}
                <td>
                  <div className='tw:text-xs tw:whitespace-nowrap'>
                    {r.periodStart ? new Date(r.periodStart).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                  </div>
                  <div className='tw:text-xs tw:text-muted-foreground tw:whitespace-nowrap'>
                    to {r.periodEnd ? new Date(r.periodEnd).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                  </div>
                </td>
                <td className='tw:text-right tw:whitespace-nowrap'>
                  <span className='tw:font-medium'>{r.quantity || '—'}</span>{' '}
                  <span className='tw:text-xs tw:text-muted-foreground'>{getActivityUnitLabel(r.unit)}</span>
                </td>
                <td>
                  <FactorPickerCell
                    row={row}
                    initiativeId={initiativeId}
                    onPickAlternative={onPickAlternative}
                    onRevertOverride={onRevertOverride}
                  />
                </td>
                <td>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        aria-label='Row actions'
                        variant='outline'
                        size='icon-sm'
                      >
                        <i className='fa fa-ellipsis-v' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                      <DropdownMenuItem onSelect={() => onEdit(row.id)}>
                        <i className='fa fa-edit me-2' />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => onRetriggerFactorSearch(row.id)}>
                        <i className='fa fa-rotate me-2' />
                        Re-run factor search
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem variant='destructive' onSelect={() => onDelete(row.id)}>
                        <i className='fa fa-trash me-2' />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};
